<template>
  <div class="container">
    <prismic-rich-text v-if="title" :field="title" class="title"></prismic-rich-text>
    <prismic-rich-text v-if="subtitle" :field="subtitle" class="subtitle"></prismic-rich-text>
    <table class="table">
      <thead>
        <tr>
          <th scope="col"></th>
          <th scope="col">{{ $t('main.prismic.earnings.sales') }}</th>
          <th scope="col">{{ $t('main.prismic.earnings.royalty') }}</th>
          <th scope="col" class="d-none d-md-block">
            {{ $t('main.prismic.earnings.retailPrice') }}
          </th>
          <th scope="col">{{ $t('main.prismic.earnings.earnings') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in parsedItems" :key="index" class="table-body">
          <td>
            <Picture
              class="image-product"
              :image-sizes="imageSizes"
              :url="item.image.url"
              :alt="item.image.alt"
              :width="140"
              :height="140"
            />
          </td>
          <td>
            <NumberIncrement
              :input-key="item.salesKey"
              :number-shown="item.sales"
              @input="setSale(index, $event)"
              @decrease="decreaseSales(index)"
              @increase="increaseSales(index)"
            />
          </td>
          <td>
            <NumberSlide
              class="d-none d-md-block"
              :number-shown="item.royalty"
              :slider-text="item.royalty + '%'"
              @input="(num) => slideNumber(index, num)"
            />
            <NumberIncrement
              :input-key="item.royaltyKey"
              class="d-md-none"
              :number-shown="item.royalty"
              @input="slideNumber(index, $event)"
              @decrease="decreaseRoyalty(index)"
              @increase="increaseRoyalty(index)"
            />
          </td>
          <td class="d-none d-md-table-cell">
            <span class="price">${{ roundTwoDecimals(retailPriceByIndex(index)) }}</span>
          </td>
          <td>
            <span class="earnings">
              ${{
                roundTwoDecimals(
                  (retailPriceByIndex(index) - item.originalPrice) * item.sales * 0.7
                )
              }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="subscription-container">
      <span class="subscription-text">{{ subscriptionText }}</span>
      <span class="total-earnings"> ${{ totalEarnings }} </span>
    </div>
    <p class="info-container">{{ infoText }}</p>
  </div>
</template>

<script>
import NumberIncrement from 'UI/NumberIncrement';
import NumberSlide from 'UI/NumberSlide';
import Picture from 'UI/Picture';

export default {
  components: {
    NumberIncrement,
    NumberSlide,
    Picture
  },
  props: {
    slice: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      title: '',
      subtitle: '',
      subscriptionText: '',
      infoText: '',
      parsedItems: [],
      imageSizes: [
        { resolution: 375, size: 140 },
        { resolution: 0, size: 85 }
      ]
    };
  },
  computed: {
    totalEarnings() {
      let earnings = 0;
      this.parsedItems.map((item, index) => {
        earnings += (this.retailPriceByIndex(index) - item.originalPrice) * item.sales * 0.7;
      });
      return this.roundTwoDecimals(earnings);
    }
  },
  created() {
    const { primary } = this.slice;
    const { title1, subtitle, subscription_text, info_text } = primary;
    this.title = title1[0]?.text ? title1 : '';
    this.subtitle = subtitle[0]?.text ? subtitle : '';
    this.subscriptionText = subscription_text;
    this.infoText = info_text;
    this.parsedItems = this.slice.items.map((item, idx) => {
      const { product_image, original_price, default_sales, default_royalty } = item;
      return {
        image: product_image,
        sales: default_sales || 10,
        keySales: idx,
        royalty: default_royalty || 40,
        keyRoyalty: idx,
        originalPrice: original_price
      };
    });
  },
  methods: {
    roundTwoDecimals(num) {
      return (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2);
    },
    slideNumber(index, num) {
      this.parsedItems[index].royalty = Number(num);
    },
    decreaseRoyalty(index) {
      if (this.parsedItems[index].royalty > 0) {
        this.parsedItems[index].royalty = this.parsedItems[index].royalty - 1;
        this.parsedItems[index].royaltyKey += 1;
      }
    },
    increaseRoyalty(index) {
      if (this.parsedItems[index].royalty < 100) {
        this.parsedItems[index].royalty = this.parsedItems[index].royalty + 1;
        this.parsedItems[index].royaltyKey += 1;
      }
    },
    setSale(index, num) {
      this.parsedItems[index].sales = Number(num);
    },
    decreaseSales(index) {
      if (this.parsedItems[index].sales > 0) {
        this.parsedItems[index].sales = this.parsedItems[index].sales - 1;
        this.parsedItems[index].salesKey += 1;
      }
    },
    increaseSales(index) {
      this.parsedItems[index].sales = this.parsedItems[index].sales + 1;
      this.parsedItems[index].salesKey += 1;
    },
    retailPriceByIndex(index) {
      return this.parsedItems[index].originalPrice * (1 + this.parsedItems[index].royalty / 100);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .title > * {
    font-size: 2.5em;
    margin-bottom: 1rem;
    color: #2a2a2a;
    @include sm {
      font-size: 1.8em;
    }
  }
  .subtitle > * {
    font-size: 1.2em;
    font-weight: 400;
    margin-bottom: 2rem;
    color: #2a2a2a;
    @include sm {
      margin-bottom: 1rem;
      font-size: 1em;
    }
  }
}
.table {
  position: relative;
  thead th {
    text-align: center;
    &:first-child {
      border-bottom: none;
      @include sm {
        border-bottom: 2px solid #f2f2f2;
      }
    }
    font-weight: 500;
    border-bottom: 2px solid #f2f2f2;
    @include sm {
      font-size: 1em;
    }
  }
  tbody tr:last-child td {
    &:first-child {
      border-bottom: none;
      @include sm {
        border-bottom: 2px solid #f2f2f2;
      }
    }
    border-bottom: 2px solid #f2f2f2;
  }
  th,
  td {
    border-top: none;
  }
  td {
    vertical-align: middle;
    display: table-cell;
    text-align: center;
    @include sm {
      padding: 0.3rem;
      &:first-child {
        padding-left: 0;
      }
    }
  }
  .price {
    font-size: 1.1em;
    display: inline-block;
    width: 60px;
  }
  .earnings {
    font-size: 1.6em;
    display: inline-block;
    width: 80px;
  }
  .image-product {
    ::v-deep img {
      width: 140px;
      @include md {
        width: 85px;
      }
    }
  }
}
.subscription-container {
  display: flex;
  justify-content: flex-end;
  margin-right: 25px;
  color: #3dd699;
  margin-bottom: 40px;
  .subscription-text {
    font-size: 1em;
    margin: auto 0.7em;
  }
  .total-earnings {
    font-size: 1.8em;
    display: inline-block;
    width: 100px;
  }
}
.info-container {
  font-size: 0.9em;
  color: #aaaaaa;
  text-align: center;
  margin: 10px 25px;
}
</style>
